// The paid profile, in its own card under "Where homeowners see you": what it
// adds (past sales, ads), how it works (sold property → ranking, example ad),
// and the "I'm interested" step, which offers a demo straight away.
import { type ReactNode, useState } from 'react';

import {
  Box,
  Button,
  ButtonBase,
  Collapse,
  Stack,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  ArrowDown,
  CalendarDays,
  Check,
  ChevronDown,
  type LucideIcon,
  Megaphone,
  Rocket,
  Search,
  TrendingUp,
  Trophy,
} from 'lucide-react';

import { useLocale } from '@shared/hooks/locale';
import { customPalette } from '@shared/theme/StyleProvider';

import { getCountryShowcase } from './claimShowcaseData';
import { useSendPaidProfileInterest } from './paidProfileInterest';
import { ExampleAd, SoldPropertyCard } from './PlacementMockups';

const IconTile = ({ icon: Icon }: { icon: LucideIcon }) => {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  return (
    <Box
      sx={{
        width: 36,
        height: 36,
        flexShrink: 0,
        borderRadius: '10px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: alpha(primary, 0.1),
        color: 'primary.main',
      }}
    >
      <Icon size={18} />
    </Box>
  );
};

const Perk = ({
  icon,
  title,
  text,
}: {
  icon: LucideIcon;
  title: string;
  text: string;
}) => (
  <Stack direction="row" spacing={1.5} sx={{ minWidth: 0 }}>
    <IconTile icon={icon} />
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 14, fontWeight: 600, lineHeight: 1.35 }}>
        {title}
      </Typography>
      <Typography
        sx={{
          mt: 0.25,
          fontSize: 13,
          lineHeight: 1.5,
          color: 'text.secondary',
        }}
      >
        {text}
      </Typography>
    </Box>
  </Stack>
);

const Showcase = ({
  title,
  text,
  children,
}: {
  title: string;
  text: string;
  children: ReactNode;
}) => (
  <Stack
    spacing={1}
    sx={{
      minWidth: 0,
      p: { xs: 1.75, sm: 2.25 },
      bgcolor: 'background.paper',
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 2,
    }}
  >
    <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{title}</Typography>
    <Typography
      sx={{ pb: 0.5, fontSize: 13, lineHeight: 1.55, color: 'text.secondary' }}
    >
      {text}
    </Typography>
    <Box
      sx={{
        p: 1.5,
        borderRadius: 2,
        bgcolor: customPalette.gray50,
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      {children}
    </Box>
  </Stack>
);

const FlowStep = ({
  icon: Icon,
  label,
}: {
  icon: LucideIcon;
  label: string;
}) => (
  <Stack
    direction="row"
    spacing={1}
    sx={{
      alignItems: 'center',
      px: 1.25,
      py: 0.875,
      bgcolor: '#fff',
      color: '#0f1116',
      border: '1px solid rgba(15,17,22,0.09)',
      borderRadius: '6px',
      '& svg': { color: '#5278DF', flexShrink: 0 },
    }}
  >
    <Icon size={15} />
    <Typography sx={{ fontSize: 12.5 }}>{label}</Typography>
  </Stack>
);

const FlowArrow = () => (
  <Box
    sx={{ display: 'flex', justifyContent: 'center', color: 'text.disabled' }}
  >
    <ArrowDown size={14} />
  </Box>
);

// The inbound demo router (cloudflare-workers demo-router.ts) sends /demo/<cc>
// to the country's BDR round robin and forwards every other parameter: the UTMs
// for attribution, name and e-mail to prefill the booking form.
const getDemoUrl = ({
  countryCode,
  language,
  email,
  name,
}: {
  countryCode: string;
  language: string;
  email: string | null;
  name: string | null;
}) => {
  const cc = countryCode.toLowerCase();
  const params = new URLSearchParams({
    utm_source: 'crm',
    utm_medium: 'referral',
    utm_campaign: `${cc}_crm_pro_paid_profile_interest`,
    lang: language,
  });
  if (email != null) {
    params.set('email', email);
  }
  if (name != null) {
    params.set('name', name);
  }
  return `https://links.realadvisor.com/demo/${cc}?${params}`;
};

type Props = {
  userId: string;
  page: 'claim' | 'takedown';
  isAgency: boolean;
  countryCode: string | null | undefined;
  agentName: string;
  agentEmail: string | null;
  agencyName: string | null;
};

export const PaidProfileCard = ({
  userId,
  page,
  isAgency,
  countryCode,
  agentName,
  agentEmail,
  agencyName,
}: Props) => {
  const { t, language } = useLocale();
  const sendPaidProfileInterest = useSendPaidProfileInterest();
  const country = getCountryShowcase(countryCode);
  const [open, setOpen] = useState(false);
  const [interested, setInterested] = useState(false);
  const demoUrl = getDemoUrl({
    countryCode: countryCode ?? 'PL',
    language,
    email: agentEmail,
    name: agentName !== '' ? agentName : null,
  });

  return (
    <Box
      sx={{
        mt: 2,
        p: { xs: 2.5, md: 4 },
        borderRadius: '12px',
        border: '1px solid rgba(82,120,223,0.22)',
        background: `linear-gradient(135deg, ${customPalette.blue50} 0%, ${customPalette.white} 70%)`,
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={{ xs: 2.5, md: 5 }}
        sx={{ alignItems: { md: 'center' } }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack
            direction="row"
            spacing={0.75}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              px: 1.25,
              py: 0.375,
              borderRadius: 999,
              bgcolor: 'primary.main',
              color: '#fff',
            }}
          >
            <Rocket size={13} />
            <Typography sx={{ fontSize: 12, fontWeight: 700 }}>
              {t('Paid profile')}
            </Typography>
          </Stack>
          <Typography
            component="h2"
            sx={{ mt: 1.25, fontSize: { xs: 19, md: 21 }, fontWeight: 600 }}
          >
            {t('Take your visibility to the next level')}
          </Typography>
          <Typography
            sx={{
              mt: 0.5,
              fontSize: 14,
              lineHeight: 1.55,
              color: 'text.secondary',
            }}
          >
            {isAgency
              ? t(
                  'Your team’s sales move you up the rankings, and our ads bring homeowners back to your agents.',
                )
              : t(
                  'Your sales move you up the rankings, and our ads bring homeowners back to you.',
                )}
          </Typography>
          <Box
            sx={{
              mt: 2.25,
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 2,
            }}
          >
            <Perk
              icon={TrendingUp}
              title={
                isAgency
                  ? t('Your team’s past sales on your pages')
                  : t('Your past sales on your profile')
              }
              text={t('Sales decide the ranking where homeowners compare.')}
            />
            <Perk
              icon={Megaphone}
              title={t('Facebook, Instagram and Google ads')}
              text={t('Homeowners who looked at agents see you again.')}
            />
          </Box>
        </Box>

        {/* Call to action, then the next step once interested */}
        <Box
          sx={{
            width: { xs: '100%', md: 300 },
            flexShrink: 0,
            p: 2.25,
            borderRadius: 2,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 6px 20px -12px rgba(0,0,0,0.25)',
          }}
        >
          {interested ? (
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <Box
                  sx={{
                    width: 26,
                    height: 26,
                    borderRadius: 999,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: customPalette.green100,
                    color: customPalette.green700,
                  }}
                >
                  <Check size={15} />
                </Box>
                <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                  {t('Thanks, we’ll be in touch shortly')}
                </Typography>
              </Stack>
              <Typography
                sx={{ fontSize: 13, lineHeight: 1.55, color: 'text.secondary' }}
              >
                {t(
                  'Want to see it now? Pick a time for a 20-minute demo with our team.',
                )}
              </Typography>
              <Button
                variant="contained"
                href={demoUrl}
                target="_blank"
                rel="noopener"
                startIcon={<CalendarDays size={16} />}
                fullWidth
              >
                {t('Book a demo')}
              </Button>
            </Stack>
          ) : (
            <Stack spacing={1.25}>
              <Typography
                sx={{ fontSize: 13, lineHeight: 1.55, color: 'text.secondary' }}
              >
                {t(
                  'Tell us you’re interested: we’ll show you what it does for agents in your area.',
                )}
              </Typography>
              <Button
                variant="contained"
                fullWidth
                onClick={() => {
                  setInterested(true);
                  if (userId !== '') {
                    sendPaidProfileInterest({
                      userId,
                      countryCode,
                      language,
                      page,
                      isAgency,
                      name: agentName,
                      agencyName,
                    });
                  }
                }}
              >
                {t('I’m interested')}
              </Button>
            </Stack>
          )}
          <ButtonBase
            onClick={() => setOpen(value => !value)}
            aria-expanded={open}
            sx={{
              mt: 1.5,
              width: '100%',
              justifyContent: 'center',
              gap: 0.5,
              fontSize: 13,
              fontWeight: 600,
              color: 'primary.main',
              borderRadius: 1,
            }}
          >
            {open ? t('Show less') : t('See how it works')}
            <Box
              sx={{
                display: 'flex',
                transform: open ? 'rotate(180deg)' : 'none',
                transition: 'transform .2s',
              }}
            >
              <ChevronDown size={15} />
            </Box>
          </ButtonBase>
        </Box>
      </Stack>

      <Collapse in={open} unmountOnExit>
        <Box
          sx={{
            mt: 3,
            display: 'grid',
            gridTemplateColumns: {
              xs: 'minmax(0, 1fr)',
              md: 'repeat(2, minmax(0, 1fr))',
            },
            gap: 2,
          }}
        >
          <Showcase
            title={t('Your sales move you up')}
            text={
              isAgency
                ? t(
                    'Sales are what ranks agencies and agents on RealAdvisor. Show your team’s past sales and you climb where homeowners compare, and on the pages Google and AI assistants read.',
                  )
                : t(
                    'Sales are what ranks agents on RealAdvisor. Show your past sales and you climb where homeowners compare agents, and on the pages Google and AI assistants read.',
                  )
            }
          >
            <Stack spacing={0.75}>
              <SoldPropertyCard
                country={country}
                agentName={agentName}
                agencyName={agencyName}
              />
              <FlowArrow />
              <FlowStep
                icon={Trophy}
                label={t('Higher in rankings and valuation results')}
              />
              <FlowArrow />
              <FlowStep
                icon={Search}
                label={t('More often on Google and in AI answers')}
              />
            </Stack>
          </Showcase>
          <Showcase
            title={t('Ads that follow homeowners')}
            text={
              isAgency
                ? t(
                    'Homeowners who looked at agents on RealAdvisor see your agents again on Facebook, Instagram and Google, with their photo, rating and sales.',
                  )
                : t(
                    'Homeowners who looked at agents on RealAdvisor see you again on Facebook, Instagram and Google, with your photo, rating and sales.',
                  )
            }
          >
            <ExampleAd country={country} />
            <Typography
              sx={{
                mt: 1,
                fontSize: 11,
                color: 'text.disabled',
                textAlign: 'center',
              }}
            >
              {t('Example ad (illustration)')}
            </Typography>
          </Showcase>
        </Box>
      </Collapse>
    </Box>
  );
};
